/**
 * IdeaCard Component
 * 
 * Displays an idea with voting controls, category badge, and metadata.
 * Optimized for list display with compact vote UI.
 */

import React, { useRef, useCallback } from 'react';
import { View, Text, Pressable, StyleSheet, Animated, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, icon, touch } from '../../../shared/tokens/mobile';
import { IdeaWithDetails, VoteType } from '../../../shared/types/idea';
import { CATEGORY_CONFIG, STATUS_CONFIG, PRIORITY_CONFIG } from './ideaConfig';
import { formatUserContent } from '../../../shared/utils/textHelpers';

interface IdeaCardProps {
  idea: IdeaWithDetails;
  onPress: () => void;
  onVote?: (voteType: VoteType | null) => void;
  voting?: boolean;
  hideBorder?: boolean;
}

// Default configs for safety
const DEFAULT_CATEGORY = { key: 'general', label: 'General', icon: 'bulb-outline', color: '#6B6B6B' };
const DEFAULT_STATUS = { key: 'open', label: 'Open', icon: 'radio-button-off-outline', color: '#4A6B82' };
const DEFAULT_PRIORITY = { key: 'medium', label: 'Medium', color: '#4A6B82' };

export function IdeaCard({ idea, onPress, onVote, voting, hideBorder }: IdeaCardProps) {
  const scale = useRef(new Animated.Value(1)).current;
  
  // Safe config access with fallbacks
  const categoryConfig = CATEGORY_CONFIG[idea.category] || DEFAULT_CATEGORY;
  const statusConfig = STATUS_CONFIG[idea.status] || DEFAULT_STATUS;
  const priorityConfig = PRIORITY_CONFIG[idea.priority] || DEFAULT_PRIORITY;

  const handlePressIn = () => {
    Animated.spring(scale, { toValue: 0.98, useNativeDriver: true, speed: 50 }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scale, { toValue: 1, useNativeDriver: true, speed: 50 }).start();
  };

  const handleUpvote = useCallback(() => {
    if (voting || !onVote) return;
    onVote(idea.user_vote === 'up' ? null : 'up');
  }, [idea.user_vote, onVote, voting]);

  const handleDownvote = useCallback(() => {
    if (voting || !onVote) return;
    onVote(idea.user_vote === 'down' ? null : 'down');
  }, [idea.user_vote, onVote, voting]);

  const authorName = idea.author.display_name || idea.author.email.split('@')[0];
  const timeAgo = getTimeAgo(new Date(idea.created_at));

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      accessibilityRole="button"
      accessibilityLabel={`${idea.title}, ${statusConfig.label}`}
    >
      <Animated.View style={[styles.container, !hideBorder && styles.border, { transform: [{ scale }] }]}>
        {/* LEFT: Vote display/controls */}
        <View style={styles.voteColumn}>
          {voting ? (
            <ActivityIndicator size="small" color={color.primary} />
          ) : onVote ? (
            <>
              <Pressable onPress={handleUpvote} hitSlop={8} style={styles.voteButton}>
                <Ionicons name={idea.user_vote === 'up' ? 'chevron-up' : 'chevron-up-outline'} size={icon.md} color={idea.user_vote === 'up' ? color.success : color.textMuted} />
              </Pressable>
              <Text style={[styles.voteScore, idea.score > 0 && styles.voteScorePositive, idea.score < 0 && styles.voteScoreNegative]}>{idea.score}</Text>
              <Pressable onPress={handleDownvote} hitSlop={8} style={styles.voteButton}>
                <Ionicons name={idea.user_vote === 'down' ? 'chevron-down' : 'chevron-down-outline'} size={icon.md} color={idea.user_vote === 'down' ? color.error : color.textMuted} />
              </Pressable>
            </>
          ) : (
            <Text style={[styles.voteScore, idea.score > 0 && styles.voteScorePositive, idea.score < 0 && styles.voteScoreNegative]}>{idea.score}</Text>
          )}
        </View>

        {/* MIDDLE: Content */}
        <View style={styles.content}>
          {/* Title */}
          <Text style={styles.title} numberOfLines={2}>{formatUserContent(idea.title)}</Text>
          
          {/* Description preview */}
          {idea.description && (
            <Text style={styles.description} numberOfLines={2}>{formatUserContent(idea.description)}</Text>
          )}
          
          {/* Meta row */}
          <View style={styles.metaRow}>
            {/* Category badge */}
            <View style={[styles.categoryBadge, { backgroundColor: categoryConfig.color + '15' }]}>
              <Ionicons name={categoryConfig.icon as any} size={12} color={categoryConfig.color} />
              <Text style={[styles.categoryText, { color: categoryConfig.color }]}>
                {categoryConfig.label}
              </Text>
            </View>
            
            {/* Priority indicator */}
            {idea.priority !== 'medium' && (
              <View style={[styles.priorityBadge, { backgroundColor: priorityConfig.color + '15' }]}>
                <Text style={[styles.priorityText, { color: priorityConfig.color }]}>
                  {priorityConfig.label}
                </Text>
              </View>
            )}
            
            {/* Comment count */}
            {idea.comment_count > 0 && (
              <View style={styles.commentBadge}>
                <Ionicons name="chatbubble-outline" size={12} color={color.textMuted} />
                <Text style={styles.commentCount}>{idea.comment_count}</Text>
              </View>
            )}
          </View>
          
          {/* Author and time */}
          <Text style={styles.authorText}>
            {authorName} • {timeAgo}
          </Text>
        </View>

        {/* RIGHT: Status indicator */}
        <View style={styles.statusColumn}>
          <View style={[styles.statusDot, { backgroundColor: statusConfig.color }]} />
        </View>
      </Animated.View>
    </Pressable>
  );
}

// =============================================================================
// HELPERS
// =============================================================================

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: space.md,
    paddingHorizontal: space.md,
    backgroundColor: color.surface,
    gap: space.sm,
  },
  border: {
    borderBottomWidth: 1,
    borderBottomColor: color.divider,
  },
  
  // Vote column
  voteColumn: {
    alignItems: 'center',
    width: 36,
    paddingTop: 2,
  },
  voteButton: {
    padding: 2,
  },
  voteScore: {
    fontSize: text.body.fontSize,
    fontWeight: '600',
    color: color.textSecondary,
    marginVertical: 2,
  },
  voteScorePositive: {
    color: color.success,
  },
  voteScoreNegative: {
    color: color.error,
  },
  
  // Content
  content: {
    flex: 1,
    gap: space.xs,
  },
  title: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    lineHeight: text.body.lineHeight,
    color: color.textPrimary,
  },
  description: {
    fontSize: text.caption.fontSize,
    lineHeight: text.caption.lineHeight,
    color: color.textSecondary,
  },
  
  // Meta row
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: space.xs,
    marginTop: space.xs,
  },
  categoryBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: space.sm,
    paddingVertical: 3,
    borderRadius: radius.full,
  },
  categoryText: {
    fontSize: 11,
    fontWeight: '600',
  },
  priorityBadge: {
    paddingHorizontal: space.sm,
    paddingVertical: 3,
    borderRadius: radius.full,
  },
  priorityText: {
    fontSize: 11,
    fontWeight: '600',
  },
  commentBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  commentCount: {
    fontSize: 11,
    color: color.textMuted,
  },
  
  // Author
  authorText: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    marginTop: space.xs,
  },
  
  // Status
  statusColumn: {
    paddingTop: 6,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
});

